import React from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { getFeatureConfigForBrand } from 'lib/getFeatureStatus';
import AIMS_FLAVORS from 'lib/aimsFlavors';
import MobileApi from 'lib/MobileApi';
import { TXT_IMG_TANDEM_HOVER, MYNEWS_ENABLED } from 'lib/brandFeatures';
import Link from 'components/Link';
import TypeIcon from 'components/TypeIcon';
import Thumbnail from 'components/Thumbnail';
import { Save } from 'components/Social/SocialShareMenu/Save';
import './styles.themed.scss';

/**
 * Maps the shared state from Redux to the component's props.
 * @param {object} state - The Redux state.
 * @param {object} state.shared - Shared state containing vertical and isChromeless properties.
 * @returns {object} Props containing vertical and isChromeless.
 */
const mapStateToProps = ({ shared }) => ({
  vertical: shared.vertical,
  isChromeless: shared.isChromeless,
});

/**
 * Functional component for rendering a related item.
 * @param {object} props - The component props.
 * @param {object} props.item - The item data to display.
 * @param {string} props.linkTracking - Tracking information for the link.
 * @param {boolean} props.singleItem - Whether the item is displayed as a single item.
 * @param {import('lib/graphqlTypeDefs.mjs').VerticalType} props.vertical  - The vertical context of the item.
 * @param {boolean} [props.isChromeless=false] - Whether the item is displayed without chrome.
 * @param {boolean} [props.isTrending=false] - Whether the item is trending.
 * @param {boolean} [props.isNBCNewsCommerce=false] - Whether the item is NBC News commerce content.
 */
const RelatedItem = ({
  item,
  linkTracking,
  singleItem,
  vertical,
  isChromeless = false,
  isTrending = false,
  isNBCNewsCommerce = false,
}) => {
  /**
   * Determines if the item's URL is external.
   * @returns {boolean} True if the URL is external, false otherwise.
   */
  const isExternalURL = () => {
    const { url } = item;
    return !/\.(nbcnews|think|msnbc)\.com/.test(url);
  };

  /**
   * Checks if the item's URL belongs to NBC News Think content.
   * @returns {boolean} True if the URL is Think content, false otherwise.
   */
  const isThinkContent = () => {
    const { url } = item;
    return /nbcnews.com\/think\//.test(url);
  };

  /**
   * Generates the unibrow element for the related item.
   * @param {string} linkStyle - The CSS class for the link element.
   * @param {string|null} spanStyle - The CSS class for the span element.
   * @returns {JSX.Element} The unibrow element.
   */
  const getUnibrow = (linkStyle, spanStyle) => {
    const { url, unibrow, label } = item;
    let text;
    let href;

    if (isThinkContent()) {
      text = 'Opinion';
      href = '/opinion';
    } else if (unibrow?.text && unibrow.url?.primary) {
      text = unibrow.text;
      href = unibrow.url.primary;
    } else if (label) {
      text = label;
      href = url;
    } else {
      const [, , , textSegment] = url.split('/');
      text = textSegment;
      href = isExternalURL() ? url : `/${text}`;
    }

    return (
      <a className={linkStyle} href={href}>
        <span className={spanStyle}>{text}</span>
      </a>
    );
  };

  /**
   * Determines the appropriate thumbnail sizes based on the item context.
   * @returns {object} An object containing size mappings for the thumbnail.
   */
  const sizes = () => {
    if (singleItem) {
      const singleShared = {
        m: AIMS_FLAVORS.FOCAL_280x140,
        xl: AIMS_FLAVORS.FOCAL_320x160,
      };

      if (['msnbc', 'select'].includes(vertical)) {
        return { s: AIMS_FLAVORS.FOCAL_80x80, ...singleShared };
      }

      return { s: AIMS_FLAVORS.FOCAL_60x60, ...singleShared };
    }

    if (vertical === 'msnbc') {
      return {
        s: AIMS_FLAVORS.FOCAL_80x80,
      };
    }

    return {
      s: AIMS_FLAVORS.FOCAL_60x60,
      m: AIMS_FLAVORS.FOCAL_80x80,
    };
  };

  const {
    title,
    type,
    thumbnail,
    id,
    url,
    label,
  } = item;

  const hasTandemHover = getFeatureConfigForBrand(TXT_IMG_TANDEM_HOVER, vertical);
  const hasSaveButton = getFeatureConfigForBrand(MYNEWS_ENABLED, vertical)?.teaseSaveEnabled || false;

  const itemClasses = classNames('related-item', {
    'related-item__default': !isTrending && !isNBCNewsCommerce && vertical === 'today',
    'related-item__is-trending': isTrending || isNBCNewsCommerce,
    'related-item__is-nbc-news-commerce': isNBCNewsCommerce,
    'related-item__single': singleItem,
    'related-item__no-art': !thumbnail,
    'related-item__has-tandem-hover': hasTandemHover,
  });

  let Element = Link;
  let options = {
    to: url,
    icid: linkTracking,
  };

  if (type === 'video' && id && isChromeless) {
    Element = 'button';
    options = {
      /**
       * Handles the click event to view the video.
       */
      onClick: () => {
        MobileApi.viewVideo(id);
      },
    };
  }

  return (
    <div className={itemClasses}>
      {thumbnail ? (
        <div className="related-item__thumbnail-wrapper">
          {hasSaveButton && (
            <Save
              contentId={id}
              contentType={type}
              options={{ isThumbnail: true, offset: 's', showCTA: true }}
            />
          )}
          <Element
            // preserving legacy spreading during class component conversion
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...options}
            className={classNames('related-item__link related-item__picture-link')}
          >
            <Thumbnail
              responsiveFlavors={sizes()}
              additionalStyles={{
                raw: 'related-item__thumbnail__image--raw',
                thumbnail: 'related-item__thumbnail__image',
              }}
              thumbnail={thumbnail}
              label={label}
              type={type}
              url={url}
              originalWidth={60}
              originalHeight={60}
            />
          </Element>
          <span className={classNames('related-item__eyebrow', { think: isThinkContent() })}>
            {getUnibrow('related-item__label', null)}
            <Element
            // preserving legacy spreading during class component conversion
            // eslint-disable-next-line react/jsx-props-no-spreading
              {...options}
            >
              <TypeIcon
                type={type}
                className="related-item__icon"
                radius={vertical === 'today'} // Consider renaming the 'radius' prop to better reflect its boolean nature for improved clarity.
              />
            </Element>
          </span>
        </div>
      ) : null}
      <div className="related-item__info-wrapper">
        <h3 className="related-item__title">
          {getUnibrow(classNames({ think: isThinkContent() }), 'related-item__eyebrow')}
          <Element
          // preserving legacy spreading during class component conversion
          // eslint-disable-next-line react/jsx-props-no-spreading
            {...options}
            className="related-item__link"
          >
            {title}
          </Element>
        </h3>
        {hasSaveButton && !thumbnail && (
          <Save contentId={id} contentType={type} options={{ isTitle: true }} />
        )}
      </div>
    </div>
  );
};

// Disabled this lint rule to minimize impact as this component is widely used.
// eslint-disable-next-line import/no-default-export
export default connect(mapStateToProps)(RelatedItem);
